import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type MouseEvent as ReactMouseEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";
import { useNavigate } from "react-router";
import {
  BookmarkPlus,
  Crosshair,
  Eraser,
  Eye,
  EyeOff,
  GitBranch,
  Info,
  ListMusic,
  Trash2,
  Undo2,
} from "lucide-react";
import { edgeKindLabel } from "#shared/schemas/graph.ts";
import type { GraphMap, GraphMapEdge, GraphMapNode } from "#shared/schemas/graphMap.ts";
import { EntityAvatar } from "@web/components/EntityAvatar.tsx";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { Button } from "@web/components/ui/button.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import {
  useCreateGraphPath,
  useDeleteGraphPath,
  useGenresFlat,
  useGraphMap,
  useGraphPathToPlaylist,
  useGraphPaths,
  type ArtistListItem,
  type GraphPathClient,
} from "@web/features/library/api.ts";
import { cn } from "@web/lib/utils.ts";
import { useBrowseTrailStore } from "@web/stores/browseTrail.ts";
import { useDetailStore } from "@web/stores/detail.ts";
import { useGraphPathStore } from "@web/stores/graphPath.ts";
import { useUiStore } from "@web/stores/ui.ts";
import { useEntityDetail } from "@web/features/detail/api.ts";
import "./graph-map.css";

const FOCUS_ANIM_MS = 500;
const CELL_SIZE = 40;
const MIN_NODE_R = 8;
const MAX_NODE_R = 30;
const LABEL_ZOOM_THRESHOLD = 1.2;
const COVER_ART_SCREEN_R = 10;
const MINIMAP_W = 140;
const MINIMAP_H = 100;
const MIN_SCALE = 0.08;
const MAX_SCALE = 6;

interface Camera {
  x: number;
  y: number;
  scale: number;
}

interface GraphColors {
  accent: string;
  panel: string;
  line: string;
  text: string;
  muted: string;
  faint: string;
  font: string;
}

interface GraphIndex {
  nodeById: Map<string, GraphMapNode>;
  adjacency: Map<string, string[]>;
  edgeByKey: Map<string, GraphMapEdge>;
  maxTrackCount: number;
}

function edgeKey(a: string, b: string): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function plural(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

function nodeWorldRadius(trackCount: number, maxTrackCount: number): number {
  const t = Math.sqrt(trackCount / Math.max(maxTrackCount, 1));
  return MIN_NODE_R + t * (MAX_NODE_R - MIN_NODE_R);
}

function bfsDistances(adjacency: Map<string, string[]>, start: string | null): Map<string, number> {
  const dist = new Map<string, number>();
  if (!start) return dist;
  const queue: string[] = [start];
  dist.set(start, 0);
  for (let i = 0; i < queue.length; i++) {
    const id = queue[i];
    if (id === undefined) continue;
    const d = dist.get(id);
    if (d === undefined) continue;
    for (const nb of adjacency.get(id) ?? []) {
      if (!dist.has(nb)) {
        dist.set(nb, d + 1);
        queue.push(nb);
      }
    }
  }
  return dist;
}

function buildGraphIndex(map: GraphMap): GraphIndex {
  const nodeById = new Map<string, GraphMapNode>();
  let maxTrackCount = 1;
  for (const node of map.nodes) {
    nodeById.set(node.id, node);
    if (node.track_count > maxTrackCount) maxTrackCount = node.track_count;
  }

  const adjacency = new Map<string, string[]>();
  const edgeByKey = new Map<string, GraphMapEdge>();
  for (const edge of map.edges) {
    edgeByKey.set(edgeKey(edge.a, edge.b), edge);
    const listA = adjacency.get(edge.a) ?? [];
    listA.push(edge.b);
    adjacency.set(edge.a, listA);
    const listB = adjacency.get(edge.b) ?? [];
    listB.push(edge.a);
    adjacency.set(edge.b, listB);
  }

  return { nodeById, adjacency, edgeByKey, maxTrackCount };
}

function edgeSteppable(edge: GraphMapEdge | undefined): boolean {
  return edge !== undefined;
}

/** Draw edge from disc rim to disc rim so lines don't run through labels. */
function rimPoints(
  ax: number,
  ay: number,
  ar: number,
  bx: number,
  by: number,
  br: number,
): { x1: number; y1: number; x2: number; y2: number } | null {
  const dx = bx - ax;
  const dy = by - ay;
  const d = Math.hypot(dx, dy);
  if (d < 1e-3 || d <= ar + br + 0.5) return null;
  const ux = dx / d;
  const uy = dy / d;
  return {
    x1: ax + ux * ar,
    y1: ay + uy * ar,
    x2: bx - ux * br,
    y2: by - uy * br,
  };
}

function readGraphColors(el: HTMLElement): GraphColors {
  const s = getComputedStyle(el);
  return {
    accent: s.getPropertyValue("--color-accent").trim() || "#1ed760",
    panel: s.getPropertyValue("--color-panel").trim() || "#181818",
    line: s.getPropertyValue("--color-line").trim() || "#282828",
    text: s.getPropertyValue("--color-text").trim() || "#ffffff",
    muted: s.getPropertyValue("--color-muted").trim() || "#b3b3b3",
    faint: s.getPropertyValue("--color-faint").trim() || "#6a6a6a",
    font: s.getPropertyValue("font-family").trim() || "sans-serif",
  };
}

function worldToScreen(
  wx: number,
  wy: number,
  camera: Camera,
  width: number,
  height: number,
): { x: number; y: number } {
  return {
    x: (wx - camera.x) * camera.scale + width / 2,
    y: (wy - camera.y) * camera.scale + height / 2,
  };
}

function screenToWorld(
  sx: number,
  sy: number,
  camera: Camera,
  width: number,
  height: number,
): { x: number; y: number } {
  return {
    x: (sx - width / 2) / camera.scale + camera.x,
    y: (sy - height / 2) / camera.scale + camera.y,
  };
}

function fitCameraToBounds(
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  width: number,
  height: number,
  paddingPx = 72,
): Camera {
  const worldW = Math.max(bounds.maxX - bounds.minX, 40);
  const worldH = Math.max(bounds.maxY - bounds.minY, 40);
  const scaleX = (width - paddingPx * 2) / worldW;
  const scaleY = (height - paddingPx * 2) / worldH;
  const scale = Math.min(Math.max(Math.min(scaleX, scaleY), MIN_SCALE), MAX_SCALE);
  return {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2,
    scale,
  };
}

function opacityForDistance(dist: number): number {
  if (dist <= 1) return 1;
  if (dist === 2) return 0.45;
  if (dist === 3) return 0.15;
  return 0.04;
}

function shouldShowLabel(dist: number, scale: number): boolean {
  if (dist <= 1) return true;
  if (dist === 2) return scale >= LABEL_ZOOM_THRESHOLD;
  return false;
}

/** Fit a short name inside a node circle (no cover art). */
function drawNameInCircle(
  ctx: CanvasRenderingContext2D,
  name: string,
  x: number,
  y: number,
  r: number,
  fill: string,
  fontFamily: string,
  emphasis: boolean,
): void {
  if (r < 10) return;
  const maxW = r * 1.7;
  const fontSize = Math.min(emphasis ? 12 : 11, Math.max(8, r * 0.42));
  ctx.font = `${emphasis ? 600 : 500} ${fontSize}px ${fontFamily}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = fill;

  const words = name.trim().split(/\s+/);
  let line1 = name;
  let line2: string | null = null;
  if (ctx.measureText(name).width > maxW && words.length > 1) {
    // Prefer two lines over aggressive ellipsis.
    let best = 1;
    for (let i = 1; i < words.length; i++) {
      const a = words.slice(0, i).join(" ");
      if (ctx.measureText(a).width <= maxW) best = i;
      else break;
    }
    line1 = words.slice(0, best).join(" ");
    line2 = words.slice(best).join(" ");
  }

  function fit(line: string): string {
    if (ctx.measureText(line).width <= maxW) return line;
    let s = line;
    while (s.length > 1 && ctx.measureText(`${s}…`).width > maxW) {
      s = s.slice(0, -1);
    }
    return `${s}…`;
  }

  line1 = fit(line1);
  if (line2) {
    line2 = fit(line2);
    const gap = fontSize * 0.55;
    ctx.fillText(line1, x, y - gap);
    ctx.fillText(line2, x, y + gap);
  } else {
    ctx.fillText(line1, x, y);
  }
}

/** Name under a cover node — keeps the photo readable. */
function drawNameBelow(
  ctx: CanvasRenderingContext2D,
  name: string,
  x: number,
  y: number,
  r: number,
  fill: string,
  fontFamily: string,
  emphasis: boolean,
): void {
  const fontSize = Math.min(emphasis ? 12 : 11, Math.max(9, r * 0.35));
  const maxW = Math.max(r * 2.8, 48);
  ctx.font = `${emphasis ? 600 : 500} ${fontSize}px ${fontFamily}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillStyle = fill;
  let s = name.trim();
  if (ctx.measureText(s).width > maxW) {
    while (s.length > 1 && ctx.measureText(`${s}…`).width > maxW) {
      s = s.slice(0, -1);
    }
    s = `${s}…`;
  }
  ctx.fillText(s, x, y + r + 3);
}

class SpatialHash {
  private cells = new Map<string, string[]>();
  private cellSize: number;
  private nodeById: Map<string, GraphMapNode>;
  private radiusFn: (id: string) => number;

  constructor(
    cellSize: number,
    nodeById: Map<string, GraphMapNode>,
    radiusFn: (id: string) => number,
  ) {
    this.cellSize = cellSize;
    this.nodeById = nodeById;
    this.radiusFn = radiusFn;
  }

  rebuild(): void {
    this.cells.clear();
    for (const [id, node] of this.nodeById) {
      const cx = Math.floor(node.x / this.cellSize);
      const cy = Math.floor(node.y / this.cellSize);
      const key = `${cx},${cy}`;
      const list = this.cells.get(key) ?? [];
      list.push(id);
      this.cells.set(key, list);
    }
  }

  /**
   * Screen-space pick. Among focus + dist-1 (and sticky bridges), closest
   * cursor wins so the focused hub cannot steal neighbour clicks.
   */
  hitTest(input: {
    sx: number;
    sy: number;
    camera: Camera;
    width: number;
    height: number;
    focusDist: Map<string, number> | null;
    /** When set, also allow this node's own neighbourhood (hover bridge). */
    stickyHoverId: string | null;
    adjacency: Map<string, string[]>;
  }): string | null {
    const { sx, sy, camera, width, height, focusDist, stickyHoverId, adjacency } =
      input;
    const hasFocus = focusDist !== null && focusDist.size > 0;
    const stickySet =
      stickyHoverId !== null
        ? new Set([stickyHoverId, ...(adjacency.get(stickyHoverId) ?? [])])
        : null;

    let best: { id: string; screenDist: number } | null = null;

    for (const [id, node] of this.nodeById) {
      const graphDist = hasFocus ? (focusDist.get(id) ?? 99) : 0;
      const inSticky = stickySet?.has(id) === true;
      // Only focus + direct neighbours are interactive from the focus.
      // Dist 2+ is map texture — pickable only while bridging from a sticky hover.
      if (hasFocus && graphDist > 1 && !inSticky) continue;

      const sp = worldToScreen(node.x, node.y, camera, width, height);
      const drawnR = this.radiusFn(id) * camera.scale;
      // Focus keeps a tighter hit target so neighbours around it stay clickable.
      // Neighbours get a generous disc + label zone below the circle.
      const isFocus = graphDist === 0;
      const baseR = isFocus
        ? Math.max(Math.min(drawnR, 18), 12)
        : Math.max(Math.min(drawnR, 28), 20);
      const distToCentre = Math.hypot(sp.x - sx, sp.y - sy);
      // Labels sit under the node — click the name should still land the node.
      const distToLabel = Math.hypot(sp.x - sx, sp.y + baseR + 10 - sy);
      const screenDist = Math.min(distToCentre, distToLabel);
      const screenR = distToCentre <= distToLabel ? baseR : Math.max(baseR * 0.85, 16);
      if (screenDist > screenR) continue;

      // Closest cursor wins — never let the focused hub steal a clearer neighbour.
      if (!best || screenDist < best.screenDist) {
        best = { id, screenDist };
      }
    }

    return best?.id ?? null;
  }
}

class ImageCache {
  private cache = new Map<string, HTMLImageElement | "loading" | "error">();

  get(url: string | null): HTMLImageElement | null {
    if (!url) return null;
    const hit = this.cache.get(url);
    if (hit === "loading" || hit === "error") return null;
    if (hit instanceof HTMLImageElement) return hit.complete && hit.naturalWidth > 0 ? hit : null;
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    this.cache.set(url, "loading");
    img.onload = () => {
      this.cache.set(url, img);
    };
    img.onerror = () => {
      this.cache.set(url, "error");
    };
    return null;
  }
}

function openGraphEdgeDrawer(input: {
  artistId: string;
  artistLabel: string;
  withArtistId: string;
  withLabel: string;
}) {
  useBrowseTrailStore.getState().openSharedTracks({
    kind: "graph_edge",
    ...input,
  });
}

function distToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-9) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const cx = x1 + t * dx;
  const cy = y1 + t * dy;
  return Math.hypot(px - cx, py - cy);
}

/**
 * One global artist map — fixed positions, moving camera.
 */
export function ArtistGraphMap({
  artists,
}: {
  artists: ArtistListItem[];
}) {
  const openDetail = useOpenDetail();
  const hops = useGraphPathStore((s) => s.hops);
  const cursor = useGraphPathStore((s) => s.cursor);
  const discardedBranch = useGraphPathStore((s) => s.discardedBranch);
  const visitedIds = useGraphPathStore((s) => s.visitedIds);
  const genreFilterId = useGraphPathStore((s) => s.genreFilterId);
  const resetTo = useGraphPathStore((s) => s.resetTo);
  const stepTo = useGraphPathStore((s) => s.stepTo);
  const jumpTo = useGraphPathStore((s) => s.jumpTo);
  const restoreDiscarded = useGraphPathStore((s) => s.restoreDiscarded);
  const clearPath = useGraphPathStore((s) => s.clearPath);
  const setGenreFilter = useGraphPathStore((s) => s.setGenreFilter);

  const [includeOrphans, setIncludeOrphans] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [crumbHoverId, setCrumbHoverId] = useState<string | null>(null);
  const [focusOffScreen, setFocusOffScreen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const minimapRef = useRef<HTMLCanvasElement>(null);
  const sizeRef = useRef({ width: 0, height: 0 });
  const cameraRef = useRef<Camera>({ x: 0, y: 0, scale: 1 });
  const cameraTargetRef = useRef<Camera>({ x: 0, y: 0, scale: 1 });
  const cameraAnimRef = useRef<{ start: Camera; at: number } | null>(null);
  const panRef = useRef<{ active: boolean; lastX: number; lastY: number; moved: boolean }>({
    active: false,
    lastX: 0,
    lastY: 0,
    moved: false,
  });
  const lastNodePickAtRef = useRef(0);
  const cameraMovingRef = useRef(false);
  const [cameraMoving, setCameraMoving] = useState(false);

  function clearCameraMoving() {
    if (!cameraMovingRef.current) return;
    cameraMovingRef.current = false;
    setCameraMoving(false);
  }

  function markCameraMoving() {
    if (cameraMovingRef.current) return;
    cameraMovingRef.current = true;
    setCameraMoving(true);
  }
  const indexRef = useRef<GraphIndex | null>(null);
  const spatialRef = useRef<SpatialHash | null>(null);
  const imageCacheRef = useRef(new ImageCache());
  const colorsRef = useRef<GraphColors | null>(null);
  const boundsRef = useRef<GraphMap["bounds"] | null>(null);
  const rafRef = useRef<number | null>(null);
  const focusOffScreenRef = useRef(false);

  const mapQuery = useGraphMap({ includeOrphans });
  const genres = useGenresFlat();
  const detailTarget = useDetailStore((s) => s.target);
  const sheetOpen = useUiStore((s) => s.sheetOpen);
  const detailQuery = useEntityDetail(detailTarget);

  const centreHop = hops[cursor] ?? null;
  const focusId = centreHop?.type === "artist" ? centreHop.id : null;

  const graphIndex = useMemo(
    () => (mapQuery.data ? buildGraphIndex(mapQuery.data) : null),
    [mapQuery.data],
  );

  const pathTrail = useMemo(() => {
    const stepKeys = new Set<string>();
    const jumps: Array<{ a: string; b: string }> = [];
    for (let i = 0; i < cursor; i++) {
      const a = hops[i];
      const b = hops[i + 1];
      if (!a || !b) continue;
      if ((b.link ?? "step") === "jump") {
        jumps.push({ a: a.id, b: b.id });
      } else {
        stepKeys.add(edgeKey(a.id, b.id));
      }
    }
    return { stepKeys, jumps };
  }, [hops, cursor]);

  const hoveredIdRef = useRef(hoveredId);
  hoveredIdRef.current = hoveredId;
  const crumbHoverIdRef = useRef(crumbHoverId);
  crumbHoverIdRef.current = crumbHoverId;
  const genreFilterIdRef = useRef(genreFilterId);
  genreFilterIdRef.current = genreFilterId;
  const visitedIdsRef = useRef(visitedIds);
  visitedIdsRef.current = visitedIds;
  const pathEdgeKeysRef = useRef(pathTrail.stepKeys);
  pathEdgeKeysRef.current = pathTrail.stepKeys;
  const pathJumpsRef = useRef(pathTrail.jumps);
  pathJumpsRef.current = pathTrail.jumps;
  const hopsRef = useRef(hops);
  hopsRef.current = hops;
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;
  const focusIdRef = useRef(focusId);
  focusIdRef.current = focusId;
  const focusDistRef = useRef<Map<string, number>>(new Map());

  function pickNodeAtClient(clientX: number, clientY: number, el: HTMLElement): string | null {
    const spatial = spatialRef.current;
    const idx = indexRef.current;
    if (!spatial || !idx) return null;
    const rect = el.getBoundingClientRect();
    const focusIdNow = focusIdRef.current;
    const focusDist =
      focusIdNow !== null
        ? focusDistRef.current.size > 0
          ? focusDistRef.current
          : bfsDistances(idx.adjacency, focusIdNow)
        : null;
    return spatial.hitTest({
      sx: clientX - rect.left,
      sy: clientY - rect.top,
      camera: cameraRef.current,
      width: rect.width,
      height: rect.height,
      focusDist,
      stickyHoverId: hoveredIdRef.current,
      adjacency: idx.adjacency,
    });
  }
  const mapDataRef = useRef(mapQuery.data);
  mapDataRef.current = mapQuery.data;

  const hubs = useMemo(
    () =>
      [...artists]
        .filter((a) => a.collab_total > 0)
        .sort((a, b) => b.collab_total - a.collab_total || b.track_count - a.track_count)
        .slice(0, 8),
    [artists],
  );

  const radiusFn = useCallback(
    (id: string) => {
      const idx = indexRef.current;
      if (!idx) return MIN_NODE_R;
      const node = idx.nodeById.get(id);
      if (!node) return MIN_NODE_R;
      return nodeWorldRadius(node.track_count, idx.maxTrackCount);
    },
    [],
  );

  const animateCameraTo = useCallback((target: Camera) => {
    cameraTargetRef.current = target;
    cameraAnimRef.current = { start: { ...cameraRef.current }, at: performance.now() };
    if (!cameraMovingRef.current) {
      cameraMovingRef.current = true;
      setCameraMoving(true);
    }
  }, []);

  const fitFocus = useCallback(
    (id: string) => {
      const idx = indexRef.current;
      const bounds = boundsRef.current;
      const { width, height } = sizeRef.current;
      if (!idx || !bounds || width <= 0 || height <= 0) return;
      const node = idx.nodeById.get(id);
      if (!node) return;

      const neighbourIds = idx.adjacency.get(id) ?? [];
      const neighbourNodes: GraphMapNode[] = [];
      for (const nb of neighbourIds) {
        const n = idx.nodeById.get(nb);
        if (n) neighbourNodes.push(n);
      }

      const nodeR = nodeWorldRadius(node.track_count, idx.maxTrackCount);
      if (neighbourNodes.length === 0) {
        const r = Math.max(nodeR * 8, 80);
        animateCameraTo(
          fitCameraToBounds(
            { minX: node.x - r, minY: node.y - r, maxX: node.x + r, maxY: node.y + r },
            width,
            height,
          ),
        );
        return;
      }

      // Frame the typical neighbourhood — one far-flung collab must not zoom the
      // camera out to the whole component (reads as "load hang" / whole-map zoom).
      const dists = neighbourNodes
        .map((n) => Math.hypot(n.x - node.x, n.y - node.y))
        .filter((d) => d > 1)
        .sort((a, b) => a - b);
      const p70 = dists[Math.min(dists.length - 1, Math.floor(dists.length * 0.7))] ?? 80;
      const frameR = Math.max(p70 * 1.45, nodeR * 5, 70);

      let minX = node.x;
      let maxX = node.x;
      let minY = node.y;
      let maxY = node.y;
      let included = 0;
      for (const n of neighbourNodes) {
        const d = Math.hypot(n.x - node.x, n.y - node.y);
        if (d > frameR * 1.05) continue;
        included += 1;
        minX = Math.min(minX, n.x);
        maxX = Math.max(maxX, n.x);
        minY = Math.min(minY, n.y);
        maxY = Math.max(maxY, n.y);
      }
      if (included === 0) {
        minX = node.x - frameR;
        maxX = node.x + frameR;
        minY = node.y - frameR;
        maxY = node.y + frameR;
      } else {
        const pad = Math.max(nodeR + 28, frameR * 0.12);
        minX -= pad;
        maxX += pad;
        minY -= pad;
        maxY += pad;
      }

      animateCameraTo(fitCameraToBounds({ minX, minY, maxX, maxY }, width, height));
    },
    [animateCameraTo],
  );

  const fitWholeMap = useCallback(() => {
    const bounds = boundsRef.current;
    const { width, height } = sizeRef.current;
    if (!bounds || width <= 0 || height <= 0) return;
    animateCameraTo(
      fitCameraToBounds(
        {
          minX: bounds.min_x,
          minY: bounds.min_y,
          maxX: bounds.max_x,
          maxY: bounds.max_y,
        },
        width,
        height,
        48,
      ),
    );
  }, [animateCameraTo]);

  useEffect(() => {
    if (!graphIndex || !mapQuery.data) return;
    indexRef.current = graphIndex;
    boundsRef.current = mapQuery.data.bounds;
    spatialRef.current = new SpatialHash(CELL_SIZE, graphIndex.nodeById, radiusFn);
    spatialRef.current.rebuild();
  }, [graphIndex, mapQuery.data, radiusFn]);

  const fitFocusRef = useRef(fitFocus);
  fitFocusRef.current = fitFocus;

  useEffect(() => {
    if (focusId) fitFocus(focusId);
  }, [focusId, fitFocus, mapQuery.data]);

  // Fail-safe so a missed anim end can't leave the strip stuck on.
  useEffect(() => {
    if (!cameraMoving) return;
    const timer = window.setTimeout(() => {
      cameraAnimRef.current = null;
      clearCameraMoving();
    }, FOCUS_ANIM_MS + 250);
    return () => window.clearTimeout(timer);
  }, [cameraMoving]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    colorsRef.current = readGraphColors(el);

    const ro = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();
      const prevW = sizeRef.current.width;
      const prevH = sizeRef.current.height;
      sizeRef.current = { width: rect.width, height: rect.height };
      const canvas = canvasRef.current;
      const minimap = minimapRef.current;
      const dpr = window.devicePixelRatio || 1;
      if (canvas) {
        canvas.width = Math.floor(rect.width * dpr);
        canvas.height = Math.floor(rect.height * dpr);
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;
      }
      if (minimap) {
        minimap.width = Math.floor(MINIMAP_W * dpr);
        minimap.height = Math.floor(MINIMAP_H * dpr);
        minimap.style.width = `${MINIMAP_W}px`;
        minimap.style.height = `${MINIMAP_H}px`;
      }
      // First real layout after mount (table → graph) — fitFocus often ran while size was 0.
      if ((prevW <= 0 || prevH <= 0) && rect.width > 0 && rect.height > 0) {
        const id = focusIdRef.current;
        if (id) fitFocusRef.current(id);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!mapQuery.data) return;

    const tick = (now: number) => {
      const anim = cameraAnimRef.current;
      if (anim) {
        const t = Math.min(1, (now - anim.at) / FOCUS_ANIM_MS);
        const e = easeOutCubic(t);
        const cam = cameraRef.current;
        const tgt = cameraTargetRef.current;
        cam.x = lerp(anim.start.x, tgt.x, e);
        cam.y = lerp(anim.start.y, tgt.y, e);
        cam.scale = lerp(anim.start.scale, tgt.scale, e);
        if (t >= 1) {
          cameraAnimRef.current = null;
          if (cameraMovingRef.current) {
            cameraMovingRef.current = false;
            setCameraMoving(false);
          }
        }
      }

      drawFrameImpl();
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  // drawFrameImpl reads latest state via refs; map data gates the loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional stable rAF loop
  }, [mapQuery.data]);

  function effectiveDistance(
    nodeId: string,
    focusDist: Map<string, number>,
    hoverDist: Map<string, number> | null,
    hoverId: string | null,
  ): number {
    if (hoverId && hoverDist && hoverDist.has(nodeId)) {
      const hd = hoverDist.get(nodeId)!;
      if (nodeId === hoverId) return 0;
      if (hd === 1) return 1;
    }
    return focusDist.get(nodeId) ?? 99;
  }

  function drawFrameImpl(): void {
    const canvas = canvasRef.current;
    const minimap = minimapRef.current;
    const idx = indexRef.current;
    const bounds = boundsRef.current;
    const colors = colorsRef.current;
    const map = mapDataRef.current;
    if (!canvas || !idx || !bounds || !colors || !map) return;

    const focusId = focusIdRef.current;
    const hoveredId = hoveredIdRef.current;
    const crumbHoverId = crumbHoverIdRef.current;
    const genreFilterId = genreFilterIdRef.current;
    const visitedIds = visitedIdsRef.current;
    const pathEdgeKeys = pathEdgeKeysRef.current;
    const pathJumps = pathJumpsRef.current;
    const hops = hopsRef.current;
    const cursor = cursorRef.current;

    const { width, height } = sizeRef.current;
    if (width <= 0 || height <= 0) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const camera = cameraRef.current;
    const focusDist = bfsDistances(idx.adjacency, focusId);
    focusDistRef.current = focusDist;
    const activeHover = hoveredId ?? crumbHoverId;
    const hoverDist = activeHover ? bfsDistances(idx.adjacency, activeHover) : null;
    const hoverNeighbourhood = activeHover
      ? new Set([activeHover, ...(idx.adjacency.get(activeHover) ?? [])])
      : null;

    if (focusId) {
      const focusNode = idx.nodeById.get(focusId);
      if (focusNode) {
        const sp = worldToScreen(focusNode.x, focusNode.y, camera, width, height);
        const off =
          sp.x < -40 || sp.y < -40 || sp.x > width + 40 || sp.y > height + 40;
        if (off !== focusOffScreenRef.current) {
          focusOffScreenRef.current = off;
          setFocusOffScreen(off);
        }
      }
    } else if (focusOffScreenRef.current) {
      focusOffScreenRef.current = false;
      setFocusOffScreen(false);
    }

    const maxEdgeWeight = map.edges.reduce((m, e) => Math.max(m, e.weight), 1e-9);

    for (const edge of map.edges) {
      const na = idx.nodeById.get(edge.a);
      const nb = idx.nodeById.get(edge.b);
      if (!na || !nb) continue;

      const key = edgeKey(edge.a, edge.b);
      const isPath = pathEdgeKeys.has(key);
      const distA = effectiveDistance(edge.a, focusDist, hoverDist, activeHover);
      const distB = effectiveDistance(edge.b, focusDist, hoverDist, activeHover);
      const edgeDist = Math.min(distA, distB);
      const fa = focusId ? (focusDist.get(edge.a) ?? 99) : 99;
      const fb = focusId ? (focusDist.get(edge.b) ?? 99) : 99;
      const isFocusSpoke =
        focusId !== null && (edge.a === focusId || edge.b === focusId);
      const isHoverSpoke =
        activeHover !== null && (edge.a === activeHover || edge.b === activeHover);
      // Mesh between collaborators of the focus — keep greyed (never lit as a star).
      const isNbrMesh = !isFocusSpoke && !isPath && fa === 1 && fb === 1;

      let opacity = isPath ? 1 : opacityForDistance(edgeDist);
      let lineW = 0.5 + (edge.weight / maxEdgeWeight) * 2;
      let stroke = colors.line;

      if (isPath) {
        lineW = 2.5;
        stroke = colors.accent;
      } else if (isFocusSpoke && !activeHover) {
        opacity = Math.max(opacity, 0.78 + (edge.weight / maxEdgeWeight) * 0.22);
        lineW = 1.5 + (edge.weight / maxEdgeWeight) * 2.2;
        stroke = colors.muted;
      } else if (isNbrMesh) {
        opacity = Math.min(opacity, 0.12);
        lineW = 0.55;
      } else if (edgeDist <= 1) {
        opacity = Math.max(opacity, 0.22 + (edge.weight / maxEdgeWeight) * 0.3);
      }

      // Hover reveals ONLY the hovered node's spokes — not the whole neighbourhood mesh.
      if (activeHover) {
        if (isHoverSpoke && !isPath) {
          opacity = Math.max(opacity, 0.88 + (edge.weight / maxEdgeWeight) * 0.12);
          lineW = Math.max(lineW, 1.6 + (edge.weight / maxEdgeWeight) * 1.8);
          stroke = colors.text;
        } else if (!isPath) {
          opacity *= 0.22;
        }
      }

      if (genreFilterId) {
        const ga = na.genre_id === genreFilterId;
        const gb = nb.genre_id === genreFilterId;
        if (!ga && !gb) opacity *= 0.2;
      }

      if (opacity < 0.02 && !isPath) continue;

      const ra = nodeWorldRadius(na.track_count, idx.maxTrackCount) * camera.scale;
      const rb = nodeWorldRadius(nb.track_count, idx.maxTrackCount) * camera.scale;
      const p1 = worldToScreen(na.x, na.y, camera, width, height);
      const p2 = worldToScreen(nb.x, nb.y, camera, width, height);
      const rim = rimPoints(p1.x, p1.y, ra + 1, p2.x, p2.y, rb + 1);
      if (!rim) continue;

      ctx.beginPath();
      ctx.moveTo(rim.x1, rim.y1);
      ctx.lineTo(rim.x2, rim.y2);
      ctx.strokeStyle = stroke;
      ctx.lineWidth = lineW;
      ctx.setLineDash(edge.kind === "album" ? [5, 4] : []);
      ctx.globalAlpha = opacity;
      ctx.stroke();
    }
    ctx.setLineDash([]);

    // Jump hops — dashed accent, never implied as a collab edge.
    for (const jump of pathJumps) {
      const na = idx.nodeById.get(jump.a);
      const nb = idx.nodeById.get(jump.b);
      if (!na || !nb) continue;
      const p1 = worldToScreen(na.x, na.y, camera, width, height);
      const p2 = worldToScreen(nb.x, nb.y, camera, width, height);
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.strokeStyle = colors.accent;
      ctx.lineWidth = 2;
      ctx.setLineDash([3, 5]);
      ctx.globalAlpha = 0.55;
      ctx.stroke();
    }
    ctx.setLineDash([]);

    ctx.globalAlpha = 1;

    for (const node of map.nodes) {
      const dist = effectiveDistance(node.id, focusDist, hoverDist, activeHover);
      let opacity = opacityForDistance(dist);
      if (hoverNeighbourhood && !hoverNeighbourhood.has(node.id)) opacity *= 0.4;
      if (genreFilterId && node.genre_id !== genreFilterId) opacity *= 0.25;
      if (opacity < 0.02) continue;

      const worldR = nodeWorldRadius(node.track_count, idx.maxTrackCount);
      const screenR = worldR * camera.scale;
      const sp = worldToScreen(node.x, node.y, camera, width, height);
      if (sp.x + screenR < -20 || sp.x - screenR > width + 20) continue;
      if (sp.y + screenR < -20 || sp.y - screenR > height + 20) continue;

      const isFocus = node.id === focusId;
      const isHovered = node.id === activeHover;
      const visited = visitedIds.includes(node.id);
      const showLabel = shouldShowLabel(dist, camera.scale) && Boolean(node.name);
      // Cover when we have one and the circle is big enough; label sits below.
      // No cover → keep name-in-circle (existing UX).
      const img =
        screenR > COVER_ART_SCREEN_R ? imageCacheRef.current.get(node.image_url) : null;

      ctx.globalAlpha = opacity;
      ctx.beginPath();
      ctx.arc(sp.x, sp.y, Math.max(screenR, 2), 0, Math.PI * 2);

      if (img) {
        ctx.save();
        ctx.clip();
        ctx.drawImage(img, sp.x - screenR, sp.y - screenR, screenR * 2, screenR * 2);
        ctx.restore();
        if (node.genre_color) {
          ctx.globalAlpha = opacity * 0.35;
          ctx.fillStyle = node.genre_color;
          ctx.beginPath();
          ctx.arc(sp.x, sp.y, Math.max(screenR, 2), 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = opacity;
        }
        ctx.beginPath();
        ctx.arc(sp.x, sp.y, Math.max(screenR, 2), 0, Math.PI * 2);
        ctx.strokeStyle = isFocus ? colors.accent : isHovered ? colors.text : colors.line;
        ctx.lineWidth = isFocus || isHovered ? 2 : 1;
        ctx.stroke();
      } else {
        // Solid plate so in-node labels stay readable (genre as thin ring, not fill).
        ctx.fillStyle = isFocus || isHovered ? colors.panel : colors.panel;
        ctx.fill();
        if (node.genre_color) {
          ctx.beginPath();
          ctx.arc(sp.x, sp.y, Math.max(screenR, 2) - 1, 0, Math.PI * 2);
          ctx.strokeStyle = node.genre_color;
          ctx.lineWidth = 2;
          ctx.globalAlpha = opacity * 0.85;
          ctx.stroke();
          ctx.globalAlpha = opacity;
        }
        ctx.beginPath();
        ctx.arc(sp.x, sp.y, Math.max(screenR, 2), 0, Math.PI * 2);
        ctx.strokeStyle = isFocus ? colors.accent : isHovered ? colors.text : colors.line;
        ctx.lineWidth = isFocus || isHovered ? 2 : 1;
        ctx.stroke();
      }

      if (isFocus) {
        ctx.beginPath();
        ctx.arc(sp.x, sp.y, Math.max(screenR, 2) + 3, 0, Math.PI * 2);
        ctx.strokeStyle = colors.accent;
        ctx.lineWidth = 2.5;
        ctx.globalAlpha = opacity;
        ctx.stroke();
      }

      if (visited && !isFocus) {
        ctx.beginPath();
        ctx.arc(sp.x + screenR * 0.55, sp.y - screenR * 0.55, 3, 0, Math.PI * 2);
        ctx.fillStyle = colors.accent;
        ctx.globalAlpha = Math.min(1, opacity + 0.2);
        ctx.fill();
      }

      if (showLabel && node.name) {
        ctx.globalAlpha = Math.min(1, opacity + 0.15);
        const labelFill = isFocus || isHovered ? colors.text : colors.muted;
        if (img) {
          drawNameBelow(
            ctx,
            node.name,
            sp.x,
            sp.y,
            Math.max(screenR, 2),
            labelFill,
            colors.font,
            isFocus || isHovered,
          );
        } else {
          drawNameInCircle(
            ctx,
            node.name,
            sp.x,
            sp.y,
            Math.max(screenR, 2),
            labelFill,
            colors.font,
            isFocus || isHovered,
          );
        }
      }
    }

    ctx.globalAlpha = 1;

    if (minimap) {
      drawMinimap(minimap, idx, colors, focusId, hops, cursor, pathEdgeKeys);
    }
  }

  function drawMinimap(
    canvas: HTMLCanvasElement,
    idx: GraphIndex,
    colors: GraphColors,
    focus: string | null,
    trailHops: typeof hops,
    trailCursor: number,
    trailKeys: Set<string>,
  ): void {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, MINIMAP_W, MINIMAP_H);

    ctx.fillStyle = colors.panel;
    ctx.fillRect(0, 0, MINIMAP_W, MINIMAP_H);
    ctx.strokeStyle = colors.line;
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, MINIMAP_W - 1, MINIMAP_H - 1);

    // Local trail: path hops + each hop's direct neighbours — not the whole library.
    const pathIds: string[] = [];
    for (let i = 0; i <= trailCursor; i++) {
      const hop = trailHops[i];
      if (hop) pathIds.push(hop.id);
    }
    const pathSet = new Set(pathIds);
    const localIds = new Set(pathIds);
    for (const id of pathIds) {
      for (const nb of idx.adjacency.get(id) ?? []) localIds.add(nb);
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const id of localIds) {
      const node = idx.nodeById.get(id);
      if (!node) continue;
      minX = Math.min(minX, node.x);
      maxX = Math.max(maxX, node.x);
      minY = Math.min(minY, node.y);
      maxY = Math.max(maxY, node.y);
    }
    if (!Number.isFinite(minX)) return;

    const pad = 10;
    const spanX = Math.max(maxX - minX, 1);
    const spanY = Math.max(maxY - minY, 1);
    // Square-ish padding so a single node / short path still lands centred.
    const padWorld = Math.max(spanX, spanY) * 0.12 + 8;
    minX -= padWorld;
    maxX += padWorld;
    minY -= padWorld;
    maxY += padWorld;
    const worldW = maxX - minX;
    const worldH = maxY - minY;
    const scale = Math.min((MINIMAP_W - pad * 2) / worldW, (MINIMAP_H - pad * 2) / worldH);
    const ox = pad + (MINIMAP_W - pad * 2 - worldW * scale) / 2;
    const oy = pad + (MINIMAP_H - pad * 2 - worldH * scale) / 2;

    const toMini = (wx: number, wy: number) => ({
      x: ox + (wx - minX) * scale,
      y: oy + (wy - minY) * scale,
    });

    // Spokes from path nodes to their level-1 neighbours.
    ctx.lineWidth = 1;
    ctx.strokeStyle = colors.line;
    ctx.globalAlpha = 0.55;
    for (const id of pathIds) {
      const from = idx.nodeById.get(id);
      if (!from) continue;
      const p1 = toMini(from.x, from.y);
      for (const nb of idx.adjacency.get(id) ?? []) {
        if (pathSet.has(nb) && trailKeys.has(edgeKey(id, nb))) continue;
        const to = idx.nodeById.get(nb);
        if (!to) continue;
        const p2 = toMini(to.x, to.y);
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;

    // Path polyline on top.
    ctx.strokeStyle = colors.accent;
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.beginPath();
    let started = false;
    for (let i = 0; i <= trailCursor; i++) {
      const hop = trailHops[i];
      if (!hop) continue;
      const node = idx.nodeById.get(hop.id);
      if (!node) continue;
      const p = toMini(node.x, node.y);
      if (!started) {
        ctx.moveTo(p.x, p.y);
        started = true;
      } else {
        ctx.lineTo(p.x, p.y);
      }
    }
    if (started) ctx.stroke();

    // Neighbour dots, then path nodes (focus on top).
    for (const id of localIds) {
      if (pathSet.has(id)) continue;
      const node = idx.nodeById.get(id);
      if (!node) continue;
      const p = toMini(node.x, node.y);
      ctx.beginPath();
      ctx.arc(p.x, p.y, 1.75, 0, Math.PI * 2);
      ctx.fillStyle = colors.muted;
      ctx.globalAlpha = 0.75;
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    for (let i = 0; i <= trailCursor; i++) {
      const hop = trailHops[i];
      if (!hop) continue;
      const node = idx.nodeById.get(hop.id);
      if (!node) continue;
      const p = toMini(node.x, node.y);
      const isFocus = hop.id === focus;
      ctx.beginPath();
      ctx.arc(p.x, p.y, isFocus ? 3.5 : 2.5, 0, Math.PI * 2);
      ctx.fillStyle = isFocus ? colors.accent : colors.text;
      ctx.fill();
      if (isFocus) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
        ctx.strokeStyle = colors.accent;
        ctx.globalAlpha = 0.45;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }
  }

  function seedArtist(a: ArtistListItem) {
    const label = a.name ?? "Artist";
    resetTo({ type: "artist", id: a.id, label });
    markCameraMoving();
    openDetail({ type: "artist", id: a.id, label }, { mode: "reset" });
  }

  function stepArtist(id: string, label: string) {
    const startingFresh = hops.length === 0;
    const at = hops[cursor];
    const sameCentre = at?.type === "artist" && at.id === id;
    if (sameCentre) {
      lastNodePickAtRef.current = performance.now();
      setHoveredId(null);
      requestAnimationFrame(() => {
        openDetail({ type: "artist", id, label }, { mode: "push" });
      });
      return;
    }

    if (startingFresh) {
      resetTo({ type: "artist", id, label, link: "step" });
    } else {
      const fromId = at?.id ?? null;
      const edge =
        fromId && indexRef.current
          ? indexRef.current.edgeByKey.get(edgeKey(fromId, id))
          : undefined;
      const link = edgeSteppable(edge) ? "step" : "jump";
      stepTo({ type: "artist", id, label, link });
    }
    lastNodePickAtRef.current = performance.now();
    markCameraMoving();
    setHoveredId(null);
    const mode = startingFresh ? "reset" : "push";
    requestAnimationFrame(() => {
      openDetail({ type: "artist", id, label }, { mode });
    });
  }

  function onCanvasPointerDown(e: ReactPointerEvent<HTMLCanvasElement>) {
    if (e.button !== 0) return;
    panRef.current = { active: true, lastX: e.clientX, lastY: e.clientY, moved: false };
    cameraAnimRef.current = null;
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function onCanvasPointerMove(e: ReactPointerEvent<HTMLCanvasElement>) {
    const pan = panRef.current;
    if (!pan.active) {
      const hit = pickNodeAtClient(e.clientX, e.clientY, e.currentTarget);
      setHoveredId(hit);
      return;
    }
    const dx = e.clientX - pan.lastX;
    const dy = e.clientY - pan.lastY;
    if (Math.abs(dx) + Math.abs(dy) > 6) pan.moved = true;
    pan.lastX = e.clientX;
    pan.lastY = e.clientY;
    const cam = cameraRef.current;
    cam.x -= dx / cam.scale;
    cam.y -= dy / cam.scale;
  }

  function onCanvasPointerUp(e: ReactPointerEvent<HTMLCanvasElement>) {
    const pan = panRef.current;
    if (!pan.active) return;
    pan.active = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (pan.moved) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const world = screenToWorld(
      e.clientX - rect.left,
      e.clientY - rect.top,
      cameraRef.current,
      rect.width,
      rect.height,
    );

    const nodeId = pickNodeAtClient(e.clientX, e.clientY, e.currentTarget);
    if (nodeId) {
      const node = indexRef.current?.nodeById.get(nodeId);
      stepArtist(nodeId, node?.name ?? "Artist");
      return;
    }

    const idx = indexRef.current;
    if (!idx) return;
    const tol = 6 / cameraRef.current.scale;
    let bestEdge: GraphMapEdge | null = null;
    let bestD = tol;
    for (const edge of mapQuery.data?.edges ?? []) {
      const na = idx.nodeById.get(edge.a);
      const nb = idx.nodeById.get(edge.b);
      if (!na || !nb) continue;
      const d = distToSegment(world.x, world.y, na.x, na.y, nb.x, nb.y);
      if (d < bestD) {
        bestD = d;
        bestEdge = edge;
      }
    }
    if (bestEdge && focusId && centreHop) {
      const otherId = bestEdge.a === focusId ? bestEdge.b : bestEdge.b === focusId ? bestEdge.a : null;
      if (otherId) {
        const other = idx.nodeById.get(otherId);
        openGraphEdgeDrawer({
          artistId: focusId,
          artistLabel: centreHop.label,
          withArtistId: otherId,
          withLabel: other?.name ?? "Artist",
        });
      }
    }
  }

  function onCanvasDoubleClick(e: ReactMouseEvent<HTMLCanvasElement>) {
    const nodeId = pickNodeAtClient(e.clientX, e.clientY, e.currentTarget);
    if (nodeId) {
      const node = indexRef.current?.nodeById.get(nodeId);
      openDetail(
        { type: "artist", id: nodeId, label: node?.name ?? "Artist" },
        { mode: "push" },
      );
      return;
    }
    // A normal step click starts a camera pan — the second click of a double-click
    // often misses the moving node and used to blast out to the whole map.
    if (performance.now() - lastNodePickAtRef.current < 500) return;
    fitWholeMap();
  }

  function onWheel(e: ReactWheelEvent<HTMLCanvasElement>) {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const before = screenToWorld(sx, sy, cameraRef.current, rect.width, rect.height);
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    const cam = cameraRef.current;
    cam.scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, cam.scale * factor));
    const after = screenToWorld(sx, sy, cam, rect.width, rect.height);
    cam.x += before.x - after.x;
    cam.y += before.y - after.y;
    cameraAnimRef.current = null;
    clearCameraMoving();
  }

  function openFocusSheet() {
    if (!centreHop) return;
    openDetail({ type: centreHop.type, id: centreHop.id, label: centreHop.label }, { mode: "reset" });
  }

  const hoveredNode = hoveredId ? graphIndex?.nodeById.get(hoveredId) ?? null : null;
  const rawFocusEdge =
    focusId && hoveredId && graphIndex
      ? graphIndex.edgeByKey.get(edgeKey(focusId, hoveredId)) ?? null
      : null;
  const focusEdge = rawFocusEdge && edgeSteppable(rawFocusEdge) ? rawFocusEdge : null;
  const canStepHovered =
    Boolean(focusId && hoveredId && focusEdge) ||
    (Boolean(focusId && hoveredId) && focusId === hoveredId);
  const isJumpHover = Boolean(focusId && hoveredId && focusId !== hoveredId && !focusEdge);

  const detailLoading = sheetOpen && detailTarget !== null && detailQuery.isLoading;
  const showLoadBar = mapQuery.isFetching || cameraMoving || detailLoading;

  if (!centreHop) {
    return (
      <div className="relative flex h-full min-h-80 flex-col items-center justify-center gap-6 px-4">
        {showLoadBar && <GraphLoadBar />}
        <EmptyState
          title="Pick an artist to explore"
          description="The map shows your whole library at once. Click a node to step; double-click for Details."
        />
        {mapQuery.isLoading && <Skeleton className="size-24 rounded-full" />}
        {hubs.length > 0 && (
          <div className="flex max-w-2xl flex-wrap justify-center gap-2">
            {hubs.map((h) => (
              <button
                key={h.id}
                type="button"
                onClick={() => seedArtist(h)}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full bg-panel-elevated py-1.5 pr-3 pl-1.5",
                  "text-sm font-medium text-text transition-colors hover:bg-row-hover",
                )}
              >
                <EntityAvatar src={h.image_url} alt="" size="sm" rounded="full" />
                <span className="max-w-36 truncate">{h.name ?? "Unknown"}</span>
                <span className="text-xs text-faint">{h.collab_total}</span>
              </button>
            ))}
          </div>
        )}
        <SavedGraphPaths />
      </div>
    );
  }

  const focusNode = focusId ? graphIndex?.nodeById.get(focusId) : null;
  const focusLabel = focusNode?.name ?? centreHop.label;
  const linkCount = focusId ? (graphIndex?.adjacency.get(focusId)?.length ?? 0) : 0;

  return (
    <div className="relative flex h-full min-h-0 flex-col gap-3">
      {showLoadBar && <GraphLoadBar />}
      <div className="flex shrink-0 flex-wrap items-center gap-2 px-1">
        <nav aria-label="Graph path" className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
          {hops.map((hop, i) => {
            const isCurrent = i === cursor;
            const isForward = i > cursor;
            return (
              <span key={`${hop.type}:${hop.id}:${i}`} className="flex items-center gap-1">
                {i > 0 && (
                  <span
                    className="text-faint"
                    title={(hop.link ?? "step") === "jump" ? "Jump (no direct collab)" : undefined}
                  >
                    {(hop.link ?? "step") === "jump" ? "↝" : "›"}
                  </span>
                )}
                <button
                  type="button"
                  className={cn(
                    "max-w-40 truncate rounded-md px-1.5 py-0.5 text-xs font-medium",
                    isCurrent
                      ? "text-text"
                      : isForward
                        ? "text-faint hover:bg-row-hover hover:text-muted"
                        : "text-muted hover:bg-row-hover hover:text-text",
                  )}
                  onClick={() => jumpTo(i)}
                  onMouseEnter={() => setCrumbHoverId(hop.id)}
                  onMouseLeave={() => setCrumbHoverId(null)}
                >
                  {hop.label}
                </button>
              </span>
            );
          })}
          {discardedBranch.length > 0 && (
            <button
              type="button"
              className="ml-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-faint hover:bg-row-hover hover:text-muted"
              title={discardedBranch.map((h) => h.label).join(" › ")}
              onClick={() => restoreDiscarded()}
            >
              <Undo2 className="size-3" />
              ⤶ {discardedBranch.length} discarded
            </button>
          )}
        </nav>

        <div className="flex flex-wrap items-center gap-1">
          {(genres.data?.length ?? 0) > 0 && (
            <label className="flex items-center gap-1 text-xs text-muted">
              <span className="sr-only">Genre filter</span>
              <select
                className="h-7 max-w-40 rounded-md border border-line bg-panel-elevated px-2 text-xs text-text"
                value={genreFilterId ?? ""}
                onChange={(e) => setGenreFilter(e.target.value || null)}
              >
                <option value="">All genres</option>
                {(genres.data ?? []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIncludeOrphans((v) => !v)}
            title={includeOrphans ? "Hide orphan artists" : "Show orphan artists"}
          >
            {includeOrphans ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
            Orphans
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={hops.length <= 1}
            onClick={() => clearPath()}
            title="Clear path"
          >
            <Eraser className="size-3.5" />
            Clear
          </Button>
          <SavePathButton disabled={hops.length === 0} />
          <Button variant="ghost" size="sm" onClick={openFocusSheet}>
            <Info className="size-3.5" />
            Details
          </Button>
        </div>
      </div>

      <div
        ref={containerRef}
        className={cn(
          "relative min-h-0 flex-1 overflow-hidden rounded-lg bg-panel-elevated bg-graph-glow",
          "ring-1 ring-line/60",
        )}
      >
        {mapQuery.isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-panel/60">
            <Skeleton className="size-24 rounded-full" />
          </div>
        )}

        <canvas
          ref={canvasRef}
          className="absolute inset-0 size-full cursor-grab active:cursor-grabbing"
          aria-label={`Artist collaboration map centred on ${focusLabel}`}
          onPointerDown={onCanvasPointerDown}
          onPointerMove={onCanvasPointerMove}
          onPointerUp={onCanvasPointerUp}
          onPointerLeave={() => {
            panRef.current.active = false;
            setHoveredId(null);
          }}
          onDoubleClick={onCanvasDoubleClick}
          onWheel={onWheel}
        />

        <canvas
          ref={minimapRef}
          className="pointer-events-none absolute bottom-3 left-3 z-10 rounded-md ring-1 ring-line/80"
          aria-hidden
        />

        {focusOffScreen && focusId && (
          <Button
            variant="secondary"
            size="sm"
            className="absolute top-3 right-3 z-20"
            onClick={() => fitFocus(focusId)}
          >
            <Crosshair className="size-3.5" />
            Return to focus
          </Button>
        )}

        {hoveredNode && (
          <div className="absolute right-3 bottom-3 z-20 flex max-w-xs items-center gap-3 rounded-lg border border-line bg-panel px-3 py-2 shadow-lg">
            <EntityAvatar src={hoveredNode.image_url} alt="" size="md" rounded="full" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{hoveredNode.name}</p>
              {focusEdge && (
                <p className="text-xs text-faint">
                  {focusEdge.shared_count.toLocaleString()} shared ·{" "}
                  {edgeKindLabel(focusEdge.track_shared, focusEdge.album_shared)}
                </p>
              )}
              {hoveredNode.genre_name && (
                <p className="text-xs text-faint">
                  {hoveredNode.genre_name}
                  {hoveredNode.genre_source ? ` · ${hoveredNode.genre_source}` : ""}
                </p>
              )}
              <div className="mt-1.5 flex flex-wrap gap-2">
                {focusId !== hoveredNode.id && (
                  <button
                    type="button"
                    className={cn(
                      "text-xs font-semibold hover:underline",
                      canStepHovered && !isJumpHover ? "text-accent" : "text-muted",
                    )}
                    title={
                      isJumpHover
                        ? "No direct collab — jump marks a break in the path"
                        : "Step along a real collaboration"
                    }
                    onClick={() => stepArtist(hoveredNode.id, hoveredNode.name ?? "Artist")}
                  >
                    {isJumpHover ? "Jump here" : "Step here"}
                  </button>
                )}
                {focusId && focusEdge && (
                  <button
                    type="button"
                    className="text-xs font-semibold text-muted hover:text-text"
                    onClick={() =>
                      openGraphEdgeDrawer({
                        artistId: focusId,
                        artistLabel: focusLabel,
                        withArtistId: hoveredNode.id,
                        withLabel: hoveredNode.name ?? "Artist",
                      })
                    }
                  >
                    Shared items
                  </button>
                )}
                <button
                  type="button"
                  className="text-xs font-semibold text-muted hover:text-text"
                  onClick={() =>
                    openDetail(
                      { type: "artist", id: hoveredNode.id, label: hoveredNode.name ?? "Artist" },
                      { mode: "push" },
                    )
                  }
                >
                  Details
                </button>
              </div>
            </div>
          </div>
        )}

        {mapQuery.isSuccess && focusNode && !hoveredNode && (
          <p className="pointer-events-none absolute bottom-3 left-1/2 z-10 max-w-md -translate-x-1/2 text-center text-xs text-faint">
            {focusNode.track_count != null
              ? `${plural(focusNode.track_count, "track", "tracks")} in library · `
              : ""}
            {plural(linkCount, "collaborator", "collaborators")}
          </p>
        )}
      </div>

      <SavedGraphPaths />
    </div>
  );
}

function GraphLoadBar() {
  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 z-40 h-0.5 overflow-hidden bg-line/50"
      role="progressbar"
      aria-label="Loading"
      aria-busy="true"
    >
      <div className="graph-load-bar h-full w-1/3 bg-accent" />
    </div>
  );
}

function SavePathButton({ disabled }: { disabled: boolean }) {
  const hops = useGraphPathStore((s) => s.hops);
  const createPath = useCreateGraphPath();
  const [open, setOpen] = useState(false);

  function save() {
    if (hops.length === 0) return;
    const name = hops.map((h) => h.label).join(" › ");
    createPath.mutate({ name: name.slice(0, 200), hops }, { onSuccess: () => setOpen(false) });
  }

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        disabled={disabled || createPath.isPending}
        title="Save graph path"
        onClick={() => setOpen((v) => !v)}
      >
        <BookmarkPlus className="size-3.5" />
        Save path
      </Button>
      {open && (
        <div className="absolute top-full right-0 z-30 mt-1 w-56 rounded-md border border-line bg-panel-elevated p-2 shadow-lg">
          <p className="px-1 pb-2 text-xs text-muted">
            Saves this trail under Graphs. Shared tracks along hops are stored with it.
          </p>
          <Button size="sm" className="w-full" disabled={createPath.isPending} onClick={save}>
            Save current path
          </Button>
        </div>
      )}
    </div>
  );
}

function SavedGraphPaths() {
  const paths = useGraphPaths();
  const del = useDeleteGraphPath();
  const toPlaylist = useGraphPathToPlaylist();
  const resetTo = useGraphPathStore((s) => s.resetTo);
  const stepTo = useGraphPathStore((s) => s.stepTo);
  const navigate = useNavigate();
  const setArtistsView = useUiStore((s) => s.setArtistsView);

  if (!paths.data?.length) return null;

  function loadPath(path: GraphPathClient) {
    const first = path.hops[0];
    if (!first) return;
    resetTo(first);
    for (let i = 1; i < path.hops.length; i++) {
      const hop = path.hops[i];
      if (hop) stepTo(hop);
    }
    setArtistsView("graph");
  }

  function openTracks(path: GraphPathClient) {
    if (path.track_ids.length === 0) return;
    const filter = JSON.stringify({
      field: "track.id",
      cmp: "in",
      value: path.track_ids,
    });
    void navigate(`/all?filter=${encodeURIComponent(filter)}`);
  }

  return (
    <section className="shrink-0 rounded-lg border border-line bg-panel-elevated px-3 py-2">
      <h2 className="mb-2 text-xs font-semibold tracking-wide text-faint uppercase">
        Saved paths
      </h2>
      <ul className="space-y-1">
        {paths.data.map((path) => (
          <li
            key={path.id}
            className="flex items-center gap-2 rounded-md px-1 py-1 hover:bg-row-hover"
          >
            <button
              type="button"
              className="min-w-0 flex-1 truncate text-left text-sm font-medium"
              onClick={() => loadPath(path)}
              title={path.hops.map((h) => h.label).join(" › ")}
            >
              {path.name}
              <span className="ml-2 text-xs font-normal text-faint">
                {path.track_ids.length} tracks
              </span>
            </button>
            <Button
              variant="ghost"
              size="icon-sm"
              title="Open tracks in grid"
              disabled={path.track_ids.length === 0}
              onClick={() => openTracks(path)}
            >
              <GitBranch className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              title="Save as local playlist"
              disabled={path.track_ids.length === 0 || toPlaylist.isPending}
              onClick={() => toPlaylist.mutate({ id: path.id })}
            >
              <ListMusic className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              title="Delete path"
              onClick={() => del.mutate(path.id)}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
